import { zodResolver } from '@hookform/resolvers/zod';
import { DEFAULT_CHANNEL_LIMITS, type ChannelLimits } from '@nw/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Modal } from '../../components/ui/common';
import { Icon, Logo } from '../../components/ui/icon';
import { toast } from '../../components/ui/toast';
import { ApiError, api, errorMessage } from '../../lib/api';
import { useCan } from '../../lib/session';
import { qk, useSettingsSummary, type AuditEntry, type ChannelDetail, type ChannelDto, type Profile, type Restrictions } from './api';
import { QuotaSummary, useRestart } from './connection-tab';
import { ago, formatPhone, samePhone, shortTime } from './format';
import { ConfirmModal, InlineError } from './ui';

// ================= profile (D-27) =================

function profileChangeText(e: AuditEntry): { title: string; sub?: string } {
  const diff = (e.diff ?? {}) as { before?: Record<string, unknown>; after?: Record<string, unknown> };
  if (e.action === 'channel.profile.picture.set') return { title: 'הועלתה תמונת פרופיל חדשה' };
  if (e.action === 'channel.profile.picture.remove') return { title: 'הוסרה תמונת הפרופיל' };
  const after = diff.after ?? {};
  const before = diff.before ?? {};
  if ('about' in after) {
    const from = before.about ? `מ"${String(before.about)}" ` : '';
    return { title: 'עודכן הטקסט "אודות"', sub: `${from}ל"${String(after.about ?? '')}"` };
  }
  if ('pushName' in after) {
    const from = before.pushName ? `מ"${String(before.pushName)}" ` : '';
    return { title: 'עודכן שם התצוגה', sub: `${from}ל"${String(after.pushName ?? '')}"` };
  }
  return { title: 'עדכון פרופיל' };
}

function ProfileFieldModal(props: { field: 'pushName' | 'about'; profile: Profile; channelId: string; onClose: () => void }) {
  const isName = props.field === 'pushName';
  const max = isName ? 25 : 139;
  const cur = (isName ? props.profile.pushName : props.profile.about) ?? '';
  const [v, setV] = useState(cur);
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => api<Profile>(`/api/channels/${props.channelId}/profile`, { method: 'PATCH', body: { [props.field]: v.trim() } }),
    onSuccess: (p) => {
      qc.setQueryData(qk.profile(props.channelId), p);
      void qc.invalidateQueries({ queryKey: qk.channel(props.channelId) });
      toast(isName ? 'שם התצוגה עודכן בוואטסאפ' : 'הטקסט "אודות" עודכן בוואטסאפ');
      props.onClose();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const over = v.length > max;
  const disabled = over || (isName && !v.trim()) || v.trim() === cur || save.isPending;
  return (
    <Modal
      open
      title={isName ? 'שינוי שם התצוגה' : 'שינוי הטקסט "אודות"'}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn primary" disabled={disabled} onClick={() => save.mutate()}>
            {save.isPending ? 'שומרים…' : 'שמירה בוואטסאפ'}
          </button>
          <button className="btn" onClick={props.onClose}>
            ביטול
          </button>
        </>
      }
    >
      <div className="callout warn">
        <Icon name="alert" />
        <div>השינוי יופיע מיד אצל כל אנשי הקשר והקבוצות של מספר הלקוחות. המערכת לא תשנה אותו בחזרה לבד.</div>
      </div>
      <div className="field mt">
        <label htmlFor="wa2-pf">{isName ? 'שם תצוגה' : 'אודות'}</label>
        {isName ? (
          <input className="input" id="wa2-pf" value={v} maxLength={max + 10} autoComplete="off" onChange={(e) => setV(e.target.value)} />
        ) : (
          <textarea className="textarea" id="wa2-pf" style={{ minHeight: 90 }} maxLength={max + 10} value={v} onChange={(e) => setV(e.target.value)} />
        )}
        <span className={`wa2-count${over ? ' over' : ''}`}>
          {v.length}/{max}
        </span>
        {isName ? (
          <span className="help">כדאי לשמור על השם NIVISION, כדי שלקוחות יזהו מי שולח להם.</span>
        ) : (
          <span className="help">אפשר להשאיר ריק כדי להסיר את הטקסט.</span>
        )}
      </div>
    </Modal>
  );
}

function PictureModal({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const upload = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file as File);
      return api<Profile>(`/api/channels/${channelId}/profile/picture`, { method: 'POST', body: fd });
    },
    onSuccess: (p) => {
      qc.setQueryData(qk.profile(channelId), p);
      toast('תמונת הפרופיל הוחלפה בוואטסאפ');
      onClose();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Modal
      open
      title="החלפת תמונת הפרופיל"
      onClose={onClose}
      footer={
        <>
          <button
            className="btn primary"
            disabled={upload.isPending}
            onClick={() => {
              if (!file) return setErr('בוחרים קובץ תמונה קודם');
              upload.mutate();
            }}
          >
            {upload.isPending ? 'מעלים…' : 'העלאה לוואטסאפ'}
          </button>
          <button className="btn" onClick={onClose}>
            ביטול
          </button>
        </>
      }
    >
      <div className="callout warn">
        <Icon name="alert" />
        <div>התמונה תתחלף מיד אצל כל אנשי הקשר והקבוצות.</div>
      </div>
      <div className="field mt">
        <label htmlFor="wa2-pic-f">תמונה חדשה</label>
        <input
          className="input"
          type="file"
          id="wa2-pic-f"
          accept="image/jpeg,image/png"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setErr(f && f.size > 5 * 1024 * 1024 ? 'התמונה גדולה מ-5MB' : null);
            setFile(f && f.size <= 5 * 1024 * 1024 ? f : null);
          }}
        />
        <span className="help">ריבועית, לפחות 640 על 640. וואטסאפ חותך לעיגול. JPG או PNG, עד 5MB.</span>
        {err ? <span className="b2a-err">{err}</span> : null}
      </div>
    </Modal>
  );
}

export function ProfileTab({ channel }: { channel: ChannelDto }) {
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.profile(channel.id), queryFn: () => api<Profile>(`/api/channels/${channel.id}/profile`) });
  const [edit, setEdit] = useState<'pushName' | 'about' | null>(null);
  const [pic, setPic] = useState(false);
  const [removePic, setRemovePic] = useState(false);
  const remove = useMutation({
    mutationFn: () => api<Profile>(`/api/channels/${channel.id}/profile/picture`, { method: 'DELETE' }),
    onSuccess: (p) => {
      qc.setQueryData(qk.profile(channel.id), p);
      setRemovePic(false);
      toast('התמונה הוסרה מהפרופיל');
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const live = channel.status === 'ready' || channel.status === 'restricted';
  const p = q.data;
  const allowed = can('channels.profile.edit');
  const editable = Boolean(p?.editable && allowed);
  const picSet = p?.picture.set;

  return (
    <>
      <div className="callout warn mb">
        <Icon name="shield" />
        <div>
          <b>המערכת לא משנה את הפרופיל לבד.</b> שם התצוגה, "אודות" והתמונה משתנים רק כשמישהו לוחץ כאן על שמירה. השינוי מופיע מיד אצל כל מי
          שמדבר עם המספר: לקוחות, קבוצות ואנשי קשר.
        </div>
      </div>
      {live ? null : (
        <div className="callout danger mb">
          <Icon name="alert" />
          <div>
            המספר לא מחובר עכשיו. אפשר לערוך את הפרופיל רק כשהוא מחובר.{' '}
            <Link className="link" to="/whatsapp">
              לחיבור
            </Link>
          </div>
        </div>
      )}
      {q.isError ? <InlineError text={`לא הצלחנו לטעון את הפרופיל. ${errorMessage(q.error)}`} onRetry={() => void q.refetch()} /> : null}
      <div className="split">
        <div className="panel">
          <div className="panel-head">
            <h2>הפרופיל בוואטסאפ</h2>
            <span className="pill">רק מנהל־על</span>
            <span className="hint">כך הלקוחות רואים את המספר כשהם פותחים את פרטי איש הקשר.</span>
          </div>
          {!p ? (
            <div className="panel-body">
              <div className="skel" style={{ height: 180 }} />
            </div>
          ) : (
            <>
              <div className="wa2-prof">
                {p.picture.url ? (
                  <span className="wa2-prof-pic">
                    <img className="b2a-prof-img" src={p.picture.url} alt="" />
                  </span>
                ) : picSet ? (
                  <span className="wa2-prof-pic">
                    <Logo size={46} fill="#3A1D66" body="#FFD600" />
                  </span>
                ) : (
                  <span className="wa2-prof-pic none">
                    <Icon name="users" />
                  </span>
                )}
                <div>
                  <b>{p.pushName ?? channel.name}</b>
                  <p>{p.about || 'אין טקסט "אודות"'}</p>
                  <p className="ltr" style={{ textAlign: 'right' }}>
                    {formatPhone(p.phone)}
                  </p>
                </div>
              </div>
              <div className="wa2-field">
                <div className="grow">
                  <div className="lbl">שם תצוגה</div>
                  <div className="val">{p.pushName ?? '-'}</div>
                  <div className="xs muted">עד 25 תווים. נקרא מהמספר עצמו.</div>
                </div>
                {allowed ? (
                  <button className="btn sm" disabled={!editable} onClick={() => setEdit('pushName')}>
                    <Icon name="edit" />
                    שינוי השם
                  </button>
                ) : null}
              </div>
              <div className="wa2-field">
                <div className="grow">
                  <div className="lbl">אודות</div>
                  <div className="val">{p.about || 'ריק'}</div>
                  <div className="xs muted">עד 139 תווים.</div>
                </div>
                {allowed ? (
                  <button className="btn sm" disabled={!editable} onClick={() => setEdit('about')}>
                    <Icon name="edit" />
                    שינוי הטקסט
                  </button>
                ) : null}
              </div>
              <div className="wa2-field">
                <div className="grow">
                  <div className="lbl">תמונת פרופיל</div>
                  <div className="val">{picSet ? 'יש תמונה' : picSet === false ? 'אין תמונה' : 'לא ידוע'}</div>
                  <div className="xs muted">JPG או PNG ריבועי.</div>
                </div>
                {allowed ? (
                  <>
                    <button className="btn sm" disabled={!editable} onClick={() => setPic(true)}>
                      <Icon name="download" />
                      החלפת התמונה
                    </button>
                    {picSet ? (
                      <button className="btn sm ghost" disabled={!editable} onClick={() => setRemovePic(true)}>
                        הסרה
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
              {p.aboutSource !== 'live' ? (
                <div className="panel-foot small muted">
                  <Icon name="info" width={14} height={14} />
                  OpenWA לא מחזיר את טקסט "אודות" ואת התמונה הנוכחיים, ולכן מוצג מה שנשמר מכאן בפעם האחרונה. אם שינו אותם בטלפון, זה לא יופיע
                  כאן.
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="stack-lg">
          <div className="panel">
            <div className="panel-head">
              <h2>משנים רק בטלפון</h2>
            </div>
            <ul className="wa2-not">
              <li>
                <Icon name="eye" />
                <span>
                  <b>הגדרות פרטיות</b>
                  <small>נראה לאחרונה, אישורי קריאה ומי רואה את התמונה.</small>
                </span>
              </li>
              <li>
                <Icon name="groups" />
                <span>
                  <b>מי יכול לצרף את המספר לקבוצות</b>
                  <small>כדאי להשאיר "כולם", כדי שלקוחות יוכלו להוסיף את המספר לקבוצה שלהם.</small>
                </span>
              </li>
            </ul>
          </div>
          <div className="panel">
            <div className="panel-head">
              <h2>שינויים אחרונים</h2>
              <span className="hint">כל שינוי נרשם ביומן הפעולות.</span>
            </div>
            {p?.recentChanges.length ? (
              <ul className="timeline" style={{ paddingBottom: 10 }}>
                {p.recentChanges.map((e) => {
                  const t = profileChangeText(e);
                  return (
                    <li key={e.id}>
                      <span className="t-time">{shortTime(e.at)}</span>
                      <span className="t-dot" />
                      <span className="t-body">
                        {t.title}
                        {t.sub ? <small>{t.sub}</small> : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="panel-body small muted">{p ? 'עוד לא שינו את הפרופיל מכאן.' : ''}</div>
            )}
          </div>
        </div>
      </div>

      {edit && p ? <ProfileFieldModal field={edit} profile={p} channelId={channel.id} onClose={() => setEdit(null)} /> : null}
      {pic ? <PictureModal channelId={channel.id} onClose={() => setPic(false)} /> : null}
      <ConfirmModal
        open={removePic}
        title="הסרת תמונת הפרופיל"
        confirmLabel="הסרת התמונה"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setRemovePic(false)}
      >
        <p>המספר יופיע בלי תמונה אצל כל אנשי הקשר והקבוצות, עד שמעלים תמונה חדשה.</p>
      </ConfirmModal>
    </>
  );
}

// ================= limits =================

const LimitsForm = z.object({
  perMinute: z.number({ error: 'מספר בין 1 ל-30' }).int().min(1, 'לפחות 1').max(30, 'עד 30'),
  minGapSec: z.number({ error: 'מספר בין 1 ל-120' }).int().min(1, 'לפחות 1').max(120, 'עד 120'),
  jitterSec: z.number({ error: 'מספר בין 0 ל-60' }).int().min(0, 'לפחות 0').max(60, 'עד 60'),
  perGroupPerHour: z.number({ error: 'מספר בין 1 ל-200' }).int().min(1, 'לפחות 1').max(200, 'עד 200'),
  dailyCap: z.number({ error: 'מספר בין 1 ל-5000' }).int().min(1, 'לפחות 1').max(5000, 'עד 5000'),
});

export function LimitsEditor({ channel, footNote }: { channel: ChannelDto; footNote?: boolean }) {
  const can = useCan();
  const qc = useQueryClient();
  const allowed = can('channels.edit_limits');
  const form = useForm<ChannelLimits>({ resolver: zodResolver(LimitsForm), defaultValues: channel.limits });
  const { register, handleSubmit, reset, formState } = form;
  useEffect(() => {
    reset(channel.limits);
  }, [channel.limits, reset]);
  const save = useMutation({
    mutationFn: (v: ChannelLimits) => api<ChannelDto>(`/api/channels/${channel.id}/limits`, { method: 'PATCH', body: { ...v } }),
    onSuccess: () => {
      toast('הקצב נשמר. השינוי חל מההודעה הבאה');
      void qc.invalidateQueries({ queryKey: qk.channel(channel.id) });
      void qc.invalidateQueries({ queryKey: qk.channels });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const num = (name: keyof ChannelLimits, label: string) => (
    <input className="input ltr num" type="number" min={0} aria-label={label} disabled={!allowed} {...register(name, { valueAsNumber: true })} />
  );
  const err = (name: keyof ChannelLimits) => (formState.errors[name] ? <p className="err">{formState.errors[name]?.message}</p> : null);

  return (
    <form onSubmit={handleSubmit((v) => save.mutate(v))}>
      <div className="panel-body">
        <div className="callout warn mb">
          <Icon name="alert" />
          <div>המספר מדבר גם עם לקוחות. כל העלאה של הקצב מגדילה את הסיכון לחסימה. מעלים בצעדים קטנים ורק אחרי שבוע יציב.</div>
        </div>
        <div className="form-grid">
          <div className="field">
            <span className="label">קצב לדקה</span>
            <div className="wa2-inline">
              {num('perMinute', 'הודעות לדקה')}
              <span>הודעות לדקה, לכל המספר</span>
            </div>
            {err('perMinute')}
          </div>
          <div className="field">
            <span className="label">מרווח בין הודעות</span>
            <div className="wa2-inline">
              {num('minGapSec', 'שניות')}
              <span>שנ', ועוד עד</span>
              {num('jitterSec', 'שניות אקראיות')}
              <span>שנ' אקראיות</span>
            </div>
            {err('minGapSec')}
            {err('jitterSec')}
          </div>
          <div className="field">
            <span className="label">לכל קבוצה</span>
            <div className="wa2-inline">
              {num('perGroupPerHour', 'הודעות לקבוצה בשעה')}
              <span>הודעות בשעה. מעבר לזה ההתראות מתקבצות</span>
            </div>
            {err('perGroupPerHour')}
          </div>
          <div className="field">
            <span className="label">תקרה יומית</span>
            <div className="wa2-inline">
              {num('dailyCap', 'הודעות ביום')}
              <span>הודעות ביום, לכל המספר</span>
            </div>
            {err('dailyCap')}
          </div>
        </div>
      </div>
      {allowed ? (
        <div className="panel-foot">
          <button className="btn primary" disabled={save.isPending}>
            {save.isPending ? 'שומרים…' : 'שמירת הקצב'}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              reset(DEFAULT_CHANNEL_LIMITS);
              toast('הערכים חזרו לברירת המחדל. לוחצים "שמירת הקצב" כדי להחיל');
            }}
          >
            החזרה לברירת המחדל
          </button>
          {footNote !== false ? <span className="small muted">כל שינוי נרשם ביומן הפעולות.</span> : null}
        </div>
      ) : (
        <div className="panel-foot small muted">
          <Icon name="key" width={14} height={14} />
          רק מנהל־על יכול לשנות את הקצב.
        </div>
      )}
    </form>
  );
}

export function LimitsTab({ channel }: { channel: ChannelDetail }) {
  const can = useCan();
  return (
    <>
      <div className="callout info mb">
        <Icon name="shield" />
        <div>
          <b>ההגבלות האלה שומרות על המספר מחסימה.</b> הן חלות על כל מה שיוצא מהמספר: התראות, סיכומים ותשובות מהצ'אטים. כשמגיעים לתקרה,
          התראות יוצאות לפני סיכומים.
        </div>
      </div>
      <div className="grid cols-2 mb">
        <QuotaSummary channel={channel} link={false} />
        <div className="panel">
          <div className="panel-head">
            <h2>הודעה ראשונה לצ'אט חדש</h2>
          </div>
          <div className="panel-body">
            <p className="small">
              <b>מה נספר:</b> הודעה ראשונה למספר שלא דיברנו איתו אף פעם, וכל אדם שמוסיפים לקבוצה. תשובה למי שכתב קודם לא נספרת.
            </p>
            <p className="small muted mt-s">עדיף שאנשים יצטרפו בקישור הזמנה או יכתבו ראשונים, ואז הם לא נספרים.</p>
          </div>
        </div>
      </div>

      <div className="panel mb">
        <div className="panel-head">
          <h2>קצב ובלמים</h2>
          <span className="pill">רק מנהל־על</span>
          <span className="hint">
            {can('channels.edit_limits') ? 'את/ה מנהל־על, אז אפשר לערוך. אצל תפקידים אחרים השדות נעולים.' : 'השדות נעולים לתפקיד שלך.'} OpenWA לא
            מגביל הודעות לדקה או לקבוצה, ולכן המגבלות האלה נאכפות אצלנו לפני שההודעה יוצאת.
          </span>
        </div>
        <LimitsEditor channel={channel} />
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>מה קורה כשמגיעים לתקרה</h2>
        </div>
        <div className="panel-body">
          <ul className="checklist">
            <li>
              <span className="ic ok">
                <Icon name="bolt" />
              </span>
              <span className="grow">
                <b>התראות יוצאות קודם.</b> סיכומים מחכים, כי התראה דחופה יותר.
              </span>
            </li>
            <li>
              <span className="ic warn">
                <Icon name="clock" />
              </span>
              <span className="grow">
                <b>מה שלא נכנס מחכה למחר.</b> התראה שמתיישנת מעבר ל-6 שעות נחסמת ונכללת בסיכום היומי.
              </span>
            </li>
            <li>
              <span className="ic warn">
                <Icon name="chat" />
              </span>
              <span className="grow">
                <b>בצ'אטים:</b> הודעה ראשונה לצ'אט חדש נעצרת עם הסבר, ותשובות למי שכתב קודם ממשיכות לצאת.
              </span>
            </li>
            <li>
              <span className="ic warn">
                <Icon name="alert" />
              </span>
              <span className="grow">
                <b>נשלח מייל לצוות</b> כשמגיעים ל-90% מהמכסה, כדי שלא יפתיע.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

// ================= restrictions =================

const KINDS: [string, string, string, string][] = [
  ['tos_block', 'וואטסאפ סירב לחבר את המספר בטענה להפרת תנאי השימוש.', 'הכל. אי אפשר לשלוח עד שוואטסאפ מסיר את החסימה.', 'במנוע שלנו'],
  ['proxy_block', 'וואטסאפ חסם את כתובת ה-IP שממנה השרת מתחבר. המספר עצמו לא נחסם.', 'הכל, עד שמחליפים את הכתובת של השרת.', 'במנוע שלנו'],
  [
    'reachout_timelock',
    'חסימה זמנית על פתיחת שיחות חדשות, בדרך כלל אחרי יותר מדי פניות למספרים לא מוכרים.',
    'רק שיחות חדשות. תשובות והודעות לקבוצות שכבר קיימות ממשיכות לצאת.',
    "מדווחת במנוע Baileys. אצלנו תיראה כשליחות שנכשלות לצ'אטים חדשים",
  ],
];

function restrictionInfo(current: unknown): { kind: string; expiresAt: string | null } {
  const o = (current && typeof current === 'object' ? current : {}) as Record<string, unknown>;
  const kind = typeof o.kind === 'string' ? o.kind : typeof o.code === 'string' ? o.code : typeof o.type === 'string' ? o.type : 'unknown';
  const expiresAt = typeof o.expiresAt === 'string' ? o.expiresAt : typeof o.until === 'string' ? o.until : null;
  return { kind, expiresAt };
}

export function RestrictionsTab({ channel }: { channel: ChannelDetail }) {
  const q = useQuery({
    queryKey: qk.restrictions(channel.id),
    queryFn: () => api<Restrictions>(`/api/channels/${channel.id}/restrictions`),
    refetchInterval: 60_000,
  });
  const r = q.data;
  const info = r?.active ? restrictionInfo(r.current) : null;

  const card = !r ? (
    <div className="skel" style={{ height: 210 }} />
  ) : r.active && info ? (
    <div className="wa2-status down" role="alert">
      <div className="wa2-ic">
        <Icon name="shield" />
      </div>
      <div className="grow">
        <span className="wa2-live">
          <i />
          הגבלה פעילה
        </span>
        <h2>וואטסאפ הגביל את המספר</h2>
        <p className="muted mt-s">
          OpenWA דיווח <span className="wa2-ev">session.restriction</span> מסוג <span className="wa2-code">{info.kind}</span>
          {r.lastRestrictionAt ? ` ב-${shortTime(r.lastRestrictionAt)}` : ''}. עד מתי: {info.expiresAt ? shortTime(info.expiresAt) : 'לא ידוע'}.
        </p>
        <dl className="wa2-facts">
          <div>
            <dt>שליחה</dt>
            <dd>עצורה. {channel.queue} הודעות בתור</dd>
          </div>
          <div>
            <dt>תקלה</dt>
            <dd>קריטית{r.lastRestrictionAt ? `, פתוחה מ-${shortTime(r.lastRestrictionAt)}` : ''}</dd>
          </div>
          <div>
            <dt>מייל ודפדפן</dt>
            <dd>נשלחו לנמעני ההתראות</dd>
          </div>
        </dl>
      </div>
    </div>
  ) : (
    <div className="wa2-status" role="status">
      <div className="wa2-ic">
        <Icon name="shield" />
      </div>
      <div className="grow">
        <span className="wa2-live">
          <i />
          נבדק {ago(r.checkedAt)}
        </span>
        <h2>אין הגבלה</h2>
        <p className="muted mt-s">וואטסאפ לא הגביל את מספר הלקוחות. אם זה יקרה, OpenWA ידווח על כך מיד, והמערכת תעצור את השליחה לבד.</p>
        <dl className="wa2-facts">
          <div>
            <dt>הגבלה אחרונה</dt>
            <dd>{r.lastRestrictionAt ? shortTime(r.lastRestrictionAt) : 'לא היו מאז שהמספר חובר'}</dd>
          </div>
          <div>
            <dt>שליחות שנכשלו היום</dt>
            <dd>
              {r.failedToday} מתוך {r.attemptedToday}
            </dd>
          </div>
          <div>
            <dt>ניתוקים ב-24 שעות</dt>
            <dd>
              {r.disconnects24h.count}
              {r.disconnects24h.last ? `, ב-${shortTime(r.disconnects24h.last)}` : ''}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );

  return (
    <div className="split-wide">
      <div className="stack-lg">
        {q.isError ? <InlineError text={`לא הצלחנו לטעון את מצב ההגבלות. ${errorMessage(q.error)}`} onRetry={() => void q.refetch()} /> : null}
        {card}
        {r?.history.length ? (
          <div className="panel">
            <div className="panel-head">
              <h2>היסטוריית הגבלות</h2>
            </div>
            <ul className="timeline" style={{ paddingBottom: 10 }}>
              {r.history.map((h) => {
                const active = Boolean(h.payload?.active);
                const k = restrictionInfo(active ? h.payload?.restriction : h.payload?.previous).kind;
                return (
                  <li key={h.id}>
                    <span className="t-time">{shortTime(h.at)}</span>
                    <span className="t-dot" />
                    <span className="t-body">
                      <span className="wa2-ev">{k}</span>
                      {active ? 'וואטסאפ הגביל את המספר' : 'ההגבלה הוסרה'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        <div className="panel flush">
          <div className="panel-head">
            <h2>סוגי הגבלות</h2>
            <span className="hint">כך OpenWA מדווח על הגבלה מוואטסאפ. לכל אחת מוצג גם תאריך סיום, אם וואטסאפ מסר אותו.</span>
          </div>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>סוג</th>
                  <th>מה זה אומר</th>
                  <th>מה נחסם</th>
                  <th className="hide-sm">מתי מופיע</th>
                </tr>
              </thead>
              <tbody>
                {KINDS.map((k) => (
                  <tr key={k[0]}>
                    <td>
                      <span className="wa2-code">{k[0]}</span>
                    </td>
                    <td className="small">{k[1]}</td>
                    <td className="small">{k[2]}</td>
                    <td className="hide-sm small muted">{k[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="stack-lg">
        <div className="panel">
          <div className="panel-head">
            <h2>מה המערכת עושה</h2>
            <span className="hint">הגבלה מוואטסאפ היא תמיד תקלה קריטית.</span>
          </div>
          <div className="panel-body">
            <ul className="checklist">
              <li>
                <span className="ic warn">
                  <Icon name="power" />
                </span>
                <span className="grow">
                  <b>עוצרת את השליחה.</b> ההודעות נשמרות בתור ולא אובדות. בהגבלה על שיחות חדשות נעצרות רק פניות חדשות.
                </span>
              </li>
              <li>
                <span className="ic warn">
                  <Icon name="alert" />
                </span>
                <span className="grow">
                  <b>פותחת תקלה קריטית:</b> באנר אדום וצליל בכל מסך באתר.
                </span>
              </li>
              <li>
                <span className="ic warn">
                  <Icon name="mail" />
                </span>
                <span className="grow">
                  <b>שולחת מייל והתראה לדפדפן</b> לכל הנמענים, ומסלימה אם אף אחד לא אישר תוך 10 דקות.
                </span>
              </li>
              <li>
                <span className="ic ok">
                  <Icon name="check" />
                </span>
                <span className="grow">
                  <b>כשההגבלה מוסרת,</b> השליחה חוזרת בקצב איטי ונשלח מייל שהתקלה נפתרה.
                </span>
              </li>
            </ul>
          </div>
          <div className="panel-foot">
            <Link className="btn sm" to="/settings/notifications">
              <Icon name="settings" />
              מי מקבל התראות
            </Link>
          </div>
        </div>
        <div className="panel">
          <div className="panel-head">
            <h2>איך מקטינים את הסיכון</h2>
          </div>
          <ul className="wa2-not">
            <li>
              <Icon name="chat" />
              <span>
                <b>לא פונים למספרים שלא מכירים את המספר</b>
                <small>עונים למי שכתב, ולא שולחים הודעה ראשונה לרשימות.</small>
              </span>
            </li>
            <li>
              <Icon name="groups" />
              <span>
                <b>לא מוסיפים לקבוצות אנשים שלא מכירים את המספר</b>
                <small>עדיף שהלקוח יזמין את המספר בקישור.</small>
              </span>
            </li>
            <li>
              <Icon name="clock" />
              <span>
                <b>לא מעלים את הקצב בבת אחת</b>
                <small>
                  הקצב והתקרות נמצאים בלשונית{' '}
                  <Link className="link" to="/whatsapp/$tab" params={{ tab: 'limits' }}>
                    מכסה וקצב
                  </Link>
                  .
                </small>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ================= advanced =================

const NOT_EXPOSED: Record<string, [string, 'sparkle' | 'send' | 'globe' | 'search' | 'groups', string]> = {
  status_posting: ['פרסום סטטוס', 'sparkle', 'נראה כמו שיווק ממספר לא רשמי ומעלה את הסיכון לחסימה.'],
  broadcast_unknown_numbers: ['הפצות המוניות למספרים לא מוכרים', 'send', 'הסיבה הנפוצה ביותר לחסימה. הפצות יגיעו כמודול נפרד, ממספר ייעודי.'],
  channels: ['ערוצים', 'globe', 'לא חלק מהשימוש שלנו, ומאחד את המספר עם תוכן ציבורי.'],
  bulk_number_check: ['בדיקת מספרים בכמות', 'search', 'בדיקה אם מספרים רבים קיימים בוואטסאפ נראית לוואטסאפ כמו איסוף רשימות.'],
  group_creation: ['יצירת קבוצות', 'groups', 'המנוע שלנו לא תומך בזה. מצטרפים לקבוצה בקישור הזמנה.'],
};

function DisconnectModal({ channel, onClose }: { channel: ChannelDto; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const [totp, setTotp] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const label = channel.phone ? formatPhone(channel.phone) : channel.name;
  const typedOk = channel.phone ? samePhone(confirm, channel.phone) : confirm.trim() === channel.name;
  const logout = useMutation({
    mutationFn: () => api<{ status: string }>(`/api/channels/${channel.id}/logout`, { body: { confirm, totpCode: totp } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.channel(channel.id) });
      void qc.invalidateQueries({ queryKey: qk.channels });
      void qc.invalidateQueries({ queryKey: ['statusbar'] });
      toast('המספר נותק. כדי לחבר שוב סורקים QR מהטלפון');
      onClose();
      void navigate({ to: '/whatsapp' });
    },
    onError: (e) => {
      setErr(e instanceof ApiError && e.code === 'STEP_UP_REQUIRED' ? `${e.message}. צריך להגדיר אימות דו־שלבי למשתמש.` : errorMessage(e));
    },
  });
  return (
    <Modal
      open
      title="ניתוק המספר"
      onClose={onClose}
      footer={
        <>
          <button className="btn danger solid" disabled={!typedOk || totp.length !== 6 || logout.isPending} onClick={() => logout.mutate()}>
            {logout.isPending ? 'מנתקים…' : 'ניתוק המספר'}
          </button>
          <button className="btn" onClick={onClose}>
            ביטול
          </button>
        </>
      }
    >
      <div className="callout danger">
        <Icon name="alert" />
        <div>
          <b>לא מנתקים בלי סיבה.</b> אחרי ניתוק צריך לסרוק QR מחדש מהטלפון, וחיבור מחדש עלול להיתקע באימות passkey. יש חשבונות שאחרי זה אי
          אפשר לקשר בכלל. עד החיבור שום הודעה לא יוצאת.
        </div>
      </div>
      <p className="mt">אם ההודעות נתקעו, נסו קודם "הפעלה מחדש". היא לא מנתקת את המכשיר.</p>
      <div className="field mt">
        <label htmlFor="wa2-confirm">
          כדי לאשר, מקלידים את {channel.phone ? 'המספר' : 'שם המספר'}: <span className="ltr">{label}</span>
        </label>
        <input
          className="input ltr"
          id="wa2-confirm"
          dir="ltr"
          autoComplete="off"
          placeholder={label}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {channel.phone ? <span className="help">אפשר להקליד עם רווחים או בלי.</span> : null}
      </div>
      <div className="field mt">
        <label htmlFor="wa2-totp">קוד מאפליקציית Authenticator</label>
        <input
          className="input ltr"
          id="wa2-totp"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          maxLength={6}
          value={totp}
          onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </div>
      {err ? <span className="b2a-err">{err}</span> : null}
    </Modal>
  );
}

export function AdvancedTab({ channel }: { channel: ChannelDetail }) {
  const can = useCan();
  const qc = useQueryClient();
  const q = useSettingsSummary(channel.id);
  const restart = useRestart(channel);
  const [disconnect, setDisconnect] = useState(false);
  const register = useMutation({
    mutationFn: () => api<{ ok: true }>(`/api/channels/${channel.id}/webhook/register`, { method: 'POST' }),
    onSuccess: () => {
      toast('ה-webhook נרשם מחדש ב-OpenWA');
      void qc.invalidateQueries({ queryKey: qk.settings(channel.id) });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const s = q.data;
  const fake = s?.provider === 'fake' || channel.provider === 'fake';
  const linked = channel.status !== 'qr_required' && channel.status !== 'created';
  const webhookOk = Boolean(s?.webhook.secretConfigured && s.webhook.rejected24h === 0);

  return (
    <>
      {q.isError ? <InlineError text={`לא הצלחנו לטעון את פרטי החיבור. ${errorMessage(q.error)}`} onRetry={() => void q.refetch()} /> : null}
      <div className="grid cols-2 mb">
        <div className="stack-lg">
          <div className="panel">
            <div className="panel-head">
              <h2>מנוע</h2>
              <span className="pill">קריאה בלבד</span>
            </div>
            <ul className="wa2-kv">
              <li>
                <span>מנוע</span>
                <b>
                  <span className="wa2-mono">{fake ? 'fake' : (s?.openwa.engine ?? 'whatsapp-web.js')}</span>
                </b>
              </li>
              <li>
                <span>למה המנוע הזה</span>
                <span className="v small">{fake ? 'ספק מדומה לבדיקות. לא שולח לוואטסאפ.' : 'סיכון חסימה נמוך יותר'}</span>
              </li>
              <li>
                <span>יצירת קבוצות</span>
                <span className="v small">
                  לא נתמכת. מצטרפים בקישור הזמנה.{' '}
                  <Link className="link" to="/groups">
                    לקבוצות
                  </Link>
                </span>
              </li>
            </ul>
            <div className="panel-foot small muted">
              <Icon name="info" width={14} height={14} />
              המנוע נקבע לכל שרת OpenWA ולא לכל מספר. החלפת מנוע מחייבת לקשר מחדש את המספר בסריקת QR, ולכן לא מחליפים אותו מכאן.
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h2>OpenWA</h2>
              <div className="actions">
                <button
                  className="btn sm"
                  disabled={q.isFetching}
                  onClick={async () => {
                    const r = await q.refetch();
                    if (r.data) {
                      toast(
                        r.data.openwa.reachable
                          ? `OpenWA עונה${r.data.openwa.latencyMs != null ? `. זמן תגובה ${r.data.openwa.latencyMs}ms` : ''}`
                          : `OpenWA לא עונה${r.data.openwa.error ? `: ${r.data.openwa.error}` : ''}`,
                        r.data.openwa.reachable ? 'ok' : 'error',
                      );
                    } else toast('הבדיקה נכשלה', 'error');
                  }}
                >
                  <Icon name="refresh" />
                  בדיקת חיבור
                </button>
              </div>
            </div>
            {!s ? (
              <div className="panel-body">
                <div className="skel" style={{ height: 150 }} />
              </div>
            ) : (
              <ul className="wa2-kv">
                <li>
                  <span>זמין</span>
                  <b>
                    {s.openwa.reachable ? (
                      <span className="pill ok">
                        <span className="d" />
                        עונה{s.openwa.latencyMs != null ? `, ${s.openwa.latencyMs}ms` : ''}
                      </span>
                    ) : (
                      <span className="pill danger">
                        <span className="d" />
                        לא עונה
                      </span>
                    )}
                  </b>
                </li>
                <li>
                  <span>גרסה</span>
                  <b className="wa2-mono">{s.openwa.version ?? '-'}</b>
                </li>
                <li>
                  <span>Session</span>
                  <b className="wa2-mono">{s.session.name}</b>
                </li>
                <li>
                  <span>סטטוס</span>
                  <b>
                    <span className="wa2-mono">{s.session.providerStatus ?? s.session.status}</span>
                  </b>
                </li>
                <li>
                  <span>פעיל לאחרונה</span>
                  <b>{ago(s.session.lastSeenAt)}</b>
                </li>
              </ul>
            )}
          </div>

          {can('channels.connect') ? (
            <div className="panel">
              <div className="panel-head">
                <h2>הפעלה מחדש</h2>
              </div>
              <div className="panel-body">
                <p className="small">
                  עוצרת ומפעילה מחדש את החיבור ל-OpenWA. המכשיר נשאר מקושר ואין צורך בסריקה. לוקח עד דקה, ובזמן הזה ההודעות מחכות בתור.
                </p>
                <button className="btn mt" disabled={restart.isPending} onClick={() => restart.mutate()}>
                  <Icon name="refresh" />
                  {restart.isPending ? 'מפעילים מחדש…' : 'הפעלה מחדש'}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="stack-lg">
          <div className="panel">
            <div className="panel-head">
              <h2>Webhook מ-OpenWA</h2>
              {s ? (
                webhookOk ? (
                  <span className="pill ok">
                    <span className="d" />
                    רשום ותקין
                  </span>
                ) : (
                  <span className="pill warn">
                    <span className="d" />
                    {s.webhook.secretConfigured ? 'יש אירועים שנדחו' : 'חתימה לא מוגדרת'}
                  </span>
                )
              ) : null}
              <span className="hint">כך המערכת שלנו יודעת על הודעות נכנסות, אישורי קריאה, ניתוקים והגבלות.</span>
            </div>
            {s ? (
              <>
                <ul className="wa2-kv">
                  <li>
                    <span>כתובת</span>
                    <b className="wa2-mono">{s.webhook.path}</b>
                  </li>
                  <li>
                    <span>חתימה</span>
                    <span className="v">
                      {s.webhook.secretConfigured ? (
                        <span className="pill ok">
                          <span className="d" />
                          מוגדרת
                        </span>
                      ) : (
                        <span className="pill danger">
                          <span className="d" />
                          לא מוגדרת
                        </span>
                      )}
                      <span className="small muted">HMAC, נבדקת בכל אירוע</span>
                    </span>
                  </li>
                  <li>
                    <span>אירוע אחרון</span>
                    <b>{ago(s.webhook.lastEventAt)}</b>
                  </li>
                  <li>
                    <span>אירועים שנדחו ב-24 שעות</span>
                    <b className="num">{s.webhook.rejected24h}</b>
                  </li>
                </ul>
                <div className="panel-body" style={{ paddingTop: 12, borderTop: '1px solid var(--line)' }}>
                  <div className="small muted" style={{ marginBottom: 6 }}>
                    אירועים שנרשמנו אליהם
                  </div>
                  <div className="wa2-chips">
                    {s.webhook.events.map((e) => (
                      <span key={e}>{e}</span>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="panel-body">
                <div className="skel" style={{ height: 150 }} />
              </div>
            )}
            {can('channels.connect') ? (
              <div className="panel-foot">
                <button className="btn sm ghost" disabled={register.isPending} onClick={() => register.mutate()}>
                  <Icon name="refresh" />
                  {register.isPending ? 'רושמים…' : 'רישום מחדש'}
                </button>
              </div>
            ) : null}
          </div>

          <div className="panel">
            <div className="panel-head">
              <h2>לא זמין כאן בכוונה</h2>
              <span className="hint">OpenWA יודע לעשות את אלה, אבל הם מסכנים את מספר הלקוחות, ולכן הם לא בממשק.</span>
            </div>
            <ul className="wa2-not">
              {(s?.notExposed ?? Object.keys(NOT_EXPOSED)).map((k) => {
                const n = NOT_EXPOSED[k];
                if (!n) return null;
                return (
                  <li key={k}>
                    <Icon name={n[1]} />
                    <span>
                      <b>{n[0]}</b>
                      <small>{n[2]}</small>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {can('channels.connect') ? (
        <div className="panel wa2-danger">
          <div className="panel-head">
            <h2>ניתוק המספר</h2>
          </div>
          <div className="panel-body">
            <p className="small">
              מבטל את הקישור של OpenWA למספר. זה לא כמו הפעלה מחדש: כל השליחות נעצרות עד שסורקים QR מחדש מהטלפון, וחיבור מחדש עלול להיתקע באימות
              passkey.
            </p>
            <p className="small muted mt-s">לא מנתקים בלי סיבה. אם יש תקלה, מתחילים מ"הפעלה מחדש".</p>
            <button className="btn danger mt" disabled={!linked} onClick={() => setDisconnect(true)}>
              <Icon name="power" />
              ניתוק המספר
            </button>
          </div>
        </div>
      ) : null}
      {disconnect ? <DisconnectModal channel={channel} onClose={() => setDisconnect(false)} /> : null}
    </>
  );
}
